import React, { useCallback, useState, useRef } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/initialize';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import SSCIdSelector from '../../../SSCIdSelector/SSCIdSelector';
import { changeOfRegDetailsSchema, changeOfRegDetailsUISchema } from '../../schemas/changeOfRegDetailsSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert';

const MpanCoreWidget = (props) => <MeterPointSelector {...props} placeholder='Select MPAN' includeSupplyDate />;
const SSCWidget = (props) => <SSCIdSelector {...props} placeholder='Select SSC' />;
const EfdDatePickerWidget = ({ value, onChange, disabled, id, formContext, enforceSupplyWindow = false }) => {
  const [focused, setFocused] = useState(false);
  const [openDirection, setOpenDirection] = useState('down');
  const containerRef = useRef(null);
  const supplyDate = enforceSupplyWindow && formContext && formContext.mpan_supply_date
    ? moment(formContext.mpan_supply_date)
    : null;
  const today = moment().endOf('day');

  const isOutsideRange = (day) => {
    if (!enforceSupplyWindow) {
      return false;
    }

    if (day.isAfter(today, 'day')) {
      return true;
    }

    if (supplyDate && supplyDate.isValid()) {
      const firstAllowedDay = supplyDate.clone().startOf('day').add(1, 'day');
      return day.isBefore(firstAllowedDay, 'day');
    }

    return false;
  };

  const getMomentValue = () => {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  };

  const handleDateChange = (date) => {
    onChange(date ? date.format('YYYY-MM-DD') : '');
  };

  const handleFocusChange = ({ focused: isFocused }) => {
    if (isFocused && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const approximateCalendarHeight = 380;

      if (spaceBelow < approximateCalendarHeight && spaceAbove > spaceBelow) {
        setOpenDirection('up');
      } else {
        setOpenDirection('down');
      }
    }

    setFocused(isFocused);
  };

  return (
    <div ref={containerRef}>
      <SingleDatePicker
        date={getMomentValue()}
        onDateChange={handleDateChange}
        focused={focused}
        onFocusChange={handleFocusChange}
        numberOfMonths={1}
        displayFormat='DD/MM/YYYY'
        placeholder='Select date'
        disabled={disabled}
        id={id || 'efd-date-picker'}
        block
        isOutsideRange={isOutsideRange}
        openDirection={openDirection}
      />
    </div>
  );
};

const RestrictedEfdDatePickerWidget = (props) => <EfdDatePickerWidget {...props} enforceSupplyWindow />;

const uiSchemaWithWidgets = {
  ...changeOfRegDetailsUISchema,
  mpan_core: {
    ...changeOfRegDetailsUISchema.mpan_core,
    'ui:widget': MpanCoreWidget,
  },
  ssc: {
    ...changeOfRegDetailsUISchema.ssc,
    'ui:widget': SSCWidget,
  },
  consent_granularity_efd: {
    ...changeOfRegDetailsUISchema.consent_granularity_efd,
    'ui:widget': EfdDatePickerWidget,
  },
  ihd_install_status_efd: {
    ...changeOfRegDetailsUISchema.ihd_install_status_efd,
    'ui:widget': EfdDatePickerWidget,
  },
  smso_efd: {
    ...changeOfRegDetailsUISchema.smso_efd,
    'ui:widget': EfdDatePickerWidget,
  },
  ssc_efd: {
    ...changeOfRegDetailsUISchema.ssc_efd,
    'ui:widget': RestrictedEfdDatePickerWidget,
  },
  pc_efd: {
    ...changeOfRegDetailsUISchema.pc_efd,
    'ui:widget': RestrictedEfdDatePickerWidget,
  },
};

const ChangeOfRegDetailsForm = ({ onClose, initialData }) => {
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});

  const validateEfdWindow = (efdValue, supplyDate) => {
    const efdTime = new Date(efdValue).getTime();
    const supplyTime = new Date(supplyDate).getTime();
    const nowTime = Date.now();
    return efdTime > supplyTime && efdTime <= nowTime;
  };

  const validateSscAndProfileClassFields = (formData) => {
    if (formData.changeType !== 'SSCandProfileClass') {
      return null;
    }

    const hasSSC = Boolean(formData.ssc && formData.ssc.trim());
    const hasPC = formData.pc !== undefined && formData.pc !== null && `${formData.pc}` !== '';
    const hasSupplyDate = Boolean(formData.mpan_supply_date);

    if (!hasSSC && !hasPC) {
      return 'Please provide either SSC or Profile Class.';
    }

    if (hasSSC && !formData.ssc_efd) {
      return 'SSC EFD is required when SSC is provided.';
    }

    if (hasPC && !formData.pc_efd) {
      return 'Profile Class EFD is required when Profile Class is provided.';
    }

    if ((hasSSC && formData.ssc_efd) || (hasPC && formData.pc_efd)) {
      if (!hasSupplyDate) {
        return 'Unable to validate EFD dates. Re-select MPAN to load supply history.';
      }
    }

    if (hasSSC && formData.ssc_efd && !validateEfdWindow(formData.ssc_efd, formData.mpan_supply_date)) {
      return 'SSC EFD must be after the meter point supply date and not later than today.';
    }

    if (hasPC && formData.pc_efd && !validateEfdWindow(formData.pc_efd, formData.mpan_supply_date)) {
      return 'Profile Class EFD must be after the meter point supply date and not later than today.';
    }

    return null;
  };

  const handleChange = useCallback(
    ({ formData }) => {
      const mpanCore = formData.mpan_core;

      if (mpanCore && typeof mpanCore === 'object') {
        updateFormContext({
          ...formData,
          mpan_core: mpanCore.name,
          mpan_supply_date: mpanCore.supplyDate || null,
        });
        return;
      }

      updateFormContext(formData);
    },
    [updateFormContext]
  );

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      // Transform field names to user-friendly labels
      switch (error.property) {
        case '.changeType':
          return { ...error, message: 'Change Type is a required property' };
        case '.mpan_core':
          return { ...error, message: 'MPAN is a required property' };
        case '.postcode':
          return { ...error, message: 'Postcode is a required property' };
        case '.address_line_1':
          return { ...error, message: 'Address Line 1 is a required property' };
        case '.is_dom_indicator':
          return { ...error, message: 'Is Dom Indicator is a required property' };
        case '.consent_granularity':
          return { ...error, message: 'Consent Granularity is a required property' };
        case '.consent_granularity_efd':
          return { ...error, message: 'Consent Granularity EFD is a required property' };
        case '.ihd_install_status':
          return { ...error, message: 'IHD Install Status is a required property' };
        case '.ihd_install_status_efd':
          return { ...error, message: 'IHD Install Status EFD is a required property' };
        case '.smso':
          return { ...error, message: 'SMSO is a required property' };
        case '.smso_efd':
          return { ...error, message: 'SMSO EFD is a required property' };
        case '.related_mpan_secondary':
          return { ...error, message: 'Related MPAN Secondary is a required property' };
        case '.linked_mpan_import_export':
          return { ...error, message: 'Linked MPAN Import/Export is a required property' };
        case '.ssc':
          return { ...error, message: 'SSC is a required property' };
        case '.ssc_efd':
          return { ...error, message: 'SSC EFD is a required property' };
        case '.pc':
          return { ...error, message: 'Profile Class is a required property' };
        case '.pc_efd':
          return { ...error, message: 'Profile Class EFD is a required property' };
        default:
          return error;
      }
    });
  }, []);

  const transformFormDataToPayload = (formData) => {
    const { changeType, mpan_core, ...otherFields } = formData;
    const mpanCoreValue = mpan_core && typeof mpan_core === 'object' ? mpan_core.name : mpan_core;

    const payload = {
      request_type: changeType,
      mpan_core: parseInt(mpanCoreValue, 10),
    };

    // Add type-specific fields
    switch (changeType) {
      case 'AddressChange':
        Object.keys(otherFields).forEach((key) => {
          if (key.startsWith('address_line_') || key === 'postcode') {
            payload[key] = otherFields[key] || null;
          }
        });
        break;

      case 'DomIndicatorChange':
        payload.is_dom_indicator = otherFields.is_dom_indicator;
        break;

      case 'ConsentGranularityChange':
        payload.consent_granularity = otherFields.consent_granularity;
        payload.consent_granularity_efd = formatDateTime(otherFields.consent_granularity_efd);
        break;

      case 'IHDStatusChange':
        payload.ihd_install_status = otherFields.ihd_install_status;
        payload.ihd_install_status_efd = formatDateTime(otherFields.ihd_install_status_efd);
        break;

      case 'SMSOChange':
        payload.smso = otherFields.smso;
        payload.smso_efd = formatDateTime(otherFields.smso_efd);
        break;

      case 'RelatedMPANAdd':
      case 'RelatedMPANCreate':
      case 'RelatedMPANRemove':
        payload.related_mpan_secondary = parseInt(otherFields.related_mpan_secondary, 10);
        break;

      case 'LinkedMPANAdd':
      case 'LinkedMPANCreate':
      case 'LinkedMPANRemove':
        payload.linked_mpan_import_export = parseInt(otherFields.linked_mpan_import_export, 10);
        break;

      case 'SSCandProfileClass':
        if (otherFields.ssc && otherFields.ssc.trim()) {
          payload.ssc = otherFields.ssc.trim();
          payload.ssc_efd = formatDateTime(otherFields.ssc_efd);
        }

        if (otherFields.pc !== undefined && otherFields.pc !== null && `${otherFields.pc}` !== '') {
          payload.pc = String(otherFields.pc).padStart(2, '0');
          payload.pc_efd = formatDateTime(otherFields.pc_efd);
        }
        break;
    }

    return payload;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;

    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateTimeString);
    const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateTimeString);

    const parsedDate = isDateOnly
      ? moment.tz(dateTimeString, 'YYYY-MM-DD', 'Europe/London')
      : hasTimezone
        ? moment.parseZone(dateTimeString)
        : moment.tz(dateTimeString, 'Europe/London');

    if (!parsedDate.isValid()) return null;

    return parsedDate.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ssZ');
  };

  const loadingIcon = isLoading ? (
    <span>
      <i className='fa fa-circle-o-notch fa-spin'></i>{' '}
    </span>
  ) : null;

  return (
    <div className='change-of-reg-details-form'>
      <Form
        schema={changeOfRegDetailsSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        showErrorList={false}
        onSubmit={({ formData }) => {
          const validationMessage = validateSscAndProfileClassFields(formData);
          if (validationMessage) {
            ReactDOM.render(
              <ExpiringAlert color='danger' message={validationMessage} />,
              document.getElementById('alert').appendChild(document.createElement('div'))
            );
            return;
          }

          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);
          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.RegDetails' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Change of Reg Details request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
              }
            })
            .catch(() => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.log('Publishing to queue:', payload);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div'))
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && loadingIcon}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChangeOfRegDetailsForm;
